<template>
  <div class="site-header-container">
    <header class="site-header">
      <div class="site-header__container container">
        <!-- Left -->
        <div class="site-header__left">
          <button
            v-if="mobile.handle"
            class="site-header__menu icon-button"
            type="button"
            @click.prevent="handleMenuToggle"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.menu')"
            />

            <icon-menu />
          </button>
          <button
            class="site-header__search icon-button"
            type="button"
            @click="openSearchOverlay"
          >
            <icon-search />

            <span
              class="text-body-s-regular"
              :class="{ 'visually-hidden': isMobile }"
              v-text="$string('general.site_header.search')"
            />
          </button>
        </div>

        <!-- Navigation -->
        <div class="site-header__navigation">
          <navigation
            v-cloak
            :blocks="blocks"
            :left="leftNavigation"
            :right="rightNavigation"
          />
        </div>

        <!-- Right -->
        <div class="site-header__right">
          <a
            class="site-header__account icon-button"
            :href="$variable('routes.account.url')"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.account')"
            />

            <icon-account />
          </a>

          <a
            class="site-header__cart icon-button"
            :href="$variable('routes.cart.url')"
            @click="handleCartToggle"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.cart')"
            />

            <icon-cart />

            <span
              v-if="cart.item_count > 0"
              class="site-header__cart-badge critical-clear text-body-2xs-regular"
              aria-hidden="true"
              v-text="cart.item_count"
            />
          </a>
        </div>
      </div>
    </header>

    <!-- Search -->
    <overlay
      key="predictiveSearch"
      block-class="predictive-search-overlay"
      direction="down"
      namespace="predictiveSearch"
      :template="false"
      type="modal"
    >
      <template #body="overlay">
        <predictive-search
          v-if="getComponentVisible('predictive-search')"
          :namespace="liquid.sectionId"
          :overlay="overlay"
        />
      </template>
    </overlay>

    <!-- Menu drawer -->
    <teleport
      v-if="mobile.handle"
      to="body"
    >
      <overlay
        key="menuDrawer"
        block-class="menu-drawer-overlay"
        direction="right"
        namespace="menuDrawer"
        type="drawer"
      >
        <template #title>
          <a
            class="menu-drawer-overlay__logo"
            :href="$variable('routes.root')"
          >
            <span
              class="visually-hidden"
              v-text="$variable('store.shopName')"
            />
            <icon-logo />
          </a>
        </template>

        <template #body="overlay">
          <menu-drawer
            v-if="getComponentVisible('menu-drawer')"
            v-cloak
            :blocks="blocks"
            :handle="mobile.handle"
            :item-count="mobile.itemCount"
            :overlay="overlay"
            :contact-url="liquid.contactUrl"
            :locations-url="liquid.locationsUrl"
          />

          <!-- Menu drawer loading placeholder -->
          <div
            v-if="!getComponentActive('menu-drawer')"
            class="site-header__menu-drawer critical-component-hide"
          >
            <div class="site-header__menu-navigation">
              <div class="site-header__menu-drawer-menus">
                <ul class="site-header__menu-drawer-menu">
                  <template
                    v-for="(_, index) of [...Array(mobile.itemCount + 1)]"
                    :key="`menu-drawer-loading-${index}`"
                  >
                    <li class="site-header__menu-drawer-menu-item">
                      <div
                        class="
                          site-header__menu-drawer-link
                          text-body-m-regular
                        "
                      >
                        <span class="site-header__menu-drawer-loading" />
                      </div>
                    </li>

                    <li class="site-header__menu-drawer-divider">
                      <divider-line />
                    </li>
                  </template>
                </ul>
              </div>
            </div>
          </div>
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>

/**
 * Vue: Site header (site-header)
 * -----------------------------------------------------------------------------
 * Site header central banner containing logo, icon buttons, and menu drawer.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} liquid - Liquid section ID, used for theme editor events.
 * @param {Object} mobile - Mobile linklist handle and item count.
 * @param {String} titleElement - HTML element of site logo based on page.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import DividerLine from '~async/utils/divider-line/divider-line'
import Overlay from '~global/overlay/overlay'

import IconAccount from '~icons/general/account.svg'
import IconCart from '~icons/general/cart.svg'
import IconLogo from '~icons/general/logo.svg'
import IconSearch from '~icons/general/search.svg'
import IconMenu from '~icons/general/menu.svg'
import Navigation from '../navigation/navigation.vue'

export default {
  name: 'SiteHeader',

  components: {
    DividerLine,
    IconAccount,
    IconCart,
    IconLogo,
    IconSearch,
    IconMenu,
    Navigation,
    MenuDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.menu-drawer' */
        '~async/menu-drawer/menu-drawer'
      ),
    }),
    PredictiveSearch: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.predictive-search' */
        '~async/predictive-search/predictive-search'
      ),
    }),
    Overlay,
  },

  props: {
    // Needed for theme editor events
    // eslint-disable-next-line vue/no-unused-properties
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        contactUrl: '',
        locationsUrl: '',
      }),
    },
    blocks: {
      type: [Boolean, Object],
      default: () => false,
    },
    mobile: {
      type: Object,
      default: () => ({
        handle: '',
        itemCount: 8,
      }),
    },
    leftNavigation: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
    rightNavigation: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
  },

  data() {
    return {
      currentMegaMenuHandle: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Handle menu toggle.
     */
    handleMenuToggle() {
      this.toggleOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    /**
     * Handle cart toggle.
     * - Prevents opening cart drawer on cart page.
     * @param {Object} event - Click event.
     */
    handleCartToggle(event) {
      if (
        this.$variable('page.type') === 'cart' ||
        !this.getComponentExists('cart-drawer')
      ) {
        return
      }

      event.preventDefault()

      this.toggleOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      })
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      if (!this.isMobile) {
        return
      }

      this.openOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.closeOverlay('menuDrawer')
    },

    /**
     * Open predictive search overlay on search submit.
     */
    openSearchOverlay() {
      this.openOverlay({
        component: 'predictive-search',
        ignoreDismissed: true,
        namespace: 'predictiveSearch',
      })
    },
  },
}
</script>
